import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  fetchWholesaleSalesReport,
  selectWholesaleSalesData,
  selectWholesaleSalesError,
  selectWholesaleSalesStatus,
} from "../features/reports/reportsSlice";
import {
  fetchStores,
  selectStores,
  selectStoresStatus,
} from "../features/stores/storesSlice";
import {
  fetchWholesaleCustomers,
  selectWholesaleCustomers,
  selectWholesaleCustomersStatus,
} from "../features/wholesale/wholesaleSlice";

function toIsoLocal(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoLocal(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function getDefaultDateRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return { startDate: toIsoLocal(first), endDate: toIsoLocal(today) };
}

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPct(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00%";
  return `${num.toFixed(2)}%`;
}

function esc(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildHtmlTable(title, rows, columns) {
  const head = columns.map((c) => `<th>${esc(c.label)}</th>`).join("");
  const body = rows
    .map((row) => {
      const tds = columns
        .map((c) => {
          const raw = row[c.key];
          const val = c.render ? c.render(raw) : raw ?? "—";
          return `<td>${esc(val)}</td>`;
        })
        .join("");
      return `<tr>${tds}</tr>`;
    })
    .join("");
  return `
    <h3>${esc(title)}</h3>
    <table border="1" cellspacing="0" cellpadding="6">
      <thead><tr>${head}</tr></thead>
      <tbody>${body || '<tr><td colspan="' + columns.length + '">Нет данных</td></tr>'}</tbody>
    </table>
  `;
}

function formatAxisMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(0)}k`;
  return String(Math.round(n));
}

function paymentTypeLabel(v) {
  const map = {
    cash: "Наличные",
    card: "Карта",
    qr: "QR",
    online: "Онлайн",
    prepaid: "Баланс предоплаты",
    none: "Не определено",
  };
  return map[v] || v || "—";
}

const PIE_COLORS = ["var(--c-s1)", "var(--c-s3)", "var(--c-success)", "var(--c-warning)", "var(--c-s5)", "var(--c-s5)"];

export default function AccountingWholesaleSalesPage() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const data = useSelector(selectWholesaleSalesData);
  const status = useSelector(selectWholesaleSalesStatus);
  const error = useSelector(selectWholesaleSalesError);
  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);
  const customers = useSelector(selectWholesaleCustomers);
  const customersStatus = useSelector(selectWholesaleCustomersStatus);

  const defaultRange = useMemo(getDefaultDateRange, []);
  const [storeId, setStoreId] = useState(searchParams.get("store_id") || "");
  const [customerId, setCustomerId] = useState(searchParams.get("customer_id") || "");
  const [startDate, setStartDate] = useState(
    searchParams.get("start_date") || defaultRange.startDate
  );
  const [endDate, setEndDate] = useState(
    searchParams.get("end_date") || defaultRange.endDate
  );

  useEffect(() => {
    if (storesStatus === "idle") dispatch(fetchStores({ pageSize: 200 }));
  }, [dispatch, storesStatus]);

  useEffect(() => {
    if (customersStatus === "idle") dispatch(fetchWholesaleCustomers());
  }, [dispatch, customersStatus]);

  useEffect(() => {
    dispatch(
      fetchWholesaleSalesReport({
        storeId: storeId || undefined,
        customerId: customerId || undefined,
        startDate,
        endDate,
      })
    );
  }, [dispatch, storeId, customerId, startDate, endDate]);

  useEffect(() => {
    const params = {};
    if (storeId) params.store_id = storeId;
    if (customerId) params.customer_id = customerId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    setSearchParams(params, { replace: true });
  }, [storeId, customerId, startDate, endDate, setSearchParams]);

  const handleApply = () => {
    dispatch(
      fetchWholesaleSalesReport({
        storeId: storeId || undefined,
        customerId: customerId || undefined,
        startDate,
        endDate,
      })
    );
  };

  const handleReset = () => {
    setStoreId("");
    setCustomerId("");
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
  };

  const totals = data?.totals || {};
  const byStore = useMemo(
    () => (Array.isArray(data?.by_store) ? data.by_store : []),
    [data]
  );
  const byCustomer = useMemo(
    () => (Array.isArray(data?.by_customer) ? data.by_customer : []),
    [data]
  );
  const daily = useMemo(
    () => (Array.isArray(data?.daily) ? data.daily : []),
    [data]
  );
  const topProducts = useMemo(
    () => (Array.isArray(data?.top_products) ? data.top_products : []),
    [data]
  );
  const paymentsByType = useMemo(
    () => (Array.isArray(data?.payments_by_type) ? data.payments_by_type : []),
    [data]
  );
  const [exportLoading, setExportLoading] = useState(null);
  const [exportError, setExportError] = useState(null);

  const dailyChartData = useMemo(
    () =>
      daily.map((d) => ({
        date: d.date,
        revenue: Number(d.revenue) || 0,
        cogs: Number(d.cogs) || 0,
        gross_profit: Number(d.gross_profit) || 0,
      })),
    [daily]
  );

  const storeChartData = useMemo(
    () =>
      byStore
        .map((s) => ({
          name: s.store_name,
          revenue: Number(s.revenue) || 0,
          gross_profit: Number(s.gross_profit) || 0,
          debt: Number(s.debt_amount) || 0,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 8),
    [byStore]
  );

  const customerDebtChartData = useMemo(
    () =>
      byCustomer
        .map((c) => ({
          name: c.customer_name,
          debt: Number(c.debt_amount) || 0,
        }))
        .filter((x) => x.debt > 0)
        .sort((a, b) => b.debt - a.debt)
        .slice(0, 10),
    [byCustomer]
  );

  const topProductsChartData = useMemo(
    () =>
      topProducts
        .map((p) => ({
          name: p.product_name || "—",
          revenue: Number(p.revenue) || 0,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10)
        .reverse(),
    [topProducts]
  );

  const paymentPieData = useMemo(
    () =>
      paymentsByType.map((p) => ({
        name: paymentTypeLabel(p.payment_type),
        value: Number(p.amount) || 0,
      })),
    [paymentsByType]
  );

  const exportMeta = useMemo(
    () => ({
      period: `${startDate} — ${endDate}`,
      store:
        storeId && stores.length
          ? stores.find((s) => String(s.id) === String(storeId))?.name || `#${storeId}`
          : "Все магазины",
      customer:
        customerId && customers.length
          ? customers.find((c) => String(c.id) === String(customerId))?.name || `#${customerId}`
          : "Все клиенты",
    }),
    [startDate, endDate, storeId, stores, customerId, customers]
  );

  const handleExport = async (format) => {
    try {
      setExportError(null);
      setExportLoading(format);
      const title = "Отчёт: Опт продажи";
      const summaryHtml = `
        <h2>${esc(title)}</h2>
        <p><b>Период:</b> ${esc(exportMeta.period)}</p>
        <p><b>Магазин:</b> ${esc(exportMeta.store)}</p>
        <p><b>Клиент:</b> ${esc(exportMeta.customer)}</p>
        <h3>Итоги</h3>
        <table border="1" cellspacing="0" cellpadding="6">
          <tr><td>Выручка</td><td>${esc(formatMoney(totals.revenue))}</td></tr>
          <tr><td>Себестоимость</td><td>${esc(formatMoney(totals.cogs))}</td></tr>
          <tr><td>Валовая прибыль</td><td>${esc(formatMoney(totals.gross_profit))}</td></tr>
          <tr><td>Маржа</td><td>${esc(formatPct(totals.margin_pct))}</td></tr>
          <tr><td>Оплачено</td><td>${esc(formatMoney(totals.paid_amount))}</td></tr>
          <tr><td>Долг</td><td>${esc(formatMoney(totals.debt_amount))}</td></tr>
        </table>
      `;

      const byStoreColumns = [
        { key: "store_name", label: "Магазин" },
        { key: "sales_count", label: "Продаж" },
        { key: "revenue", label: "Выручка", render: formatMoney },
        { key: "cogs", label: "Себестоимость", render: formatMoney },
        { key: "gross_profit", label: "Прибыль", render: formatMoney },
        { key: "margin_pct", label: "Маржа", render: formatPct },
        { key: "paid_amount", label: "Оплачено", render: formatMoney },
        { key: "debt_amount", label: "Долг", render: formatMoney },
      ];
      const byCustomerColumns = [
        { key: "customer_name", label: "Клиент" },
        { key: "sales_count", label: "Продаж" },
        { key: "revenue", label: "Выручка", render: formatMoney },
        { key: "cogs", label: "Себестоимость", render: formatMoney },
        { key: "gross_profit", label: "Прибыль", render: formatMoney },
        { key: "margin_pct", label: "Маржа", render: formatPct },
        { key: "paid_amount", label: "Оплачено", render: formatMoney },
        { key: "debt_amount", label: "Долг", render: formatMoney },
      ];
      const dailyColumns = [
        { key: "date", label: "Дата" },
        { key: "sales_count", label: "Продаж" },
        { key: "revenue", label: "Выручка", render: formatMoney },
        { key: "cogs", label: "Себестоимость", render: formatMoney },
        { key: "gross_profit", label: "Прибыль", render: formatMoney },
        { key: "margin_pct", label: "Маржа", render: formatPct },
        { key: "paid_amount", label: "Оплачено", render: formatMoney },
        { key: "debt_amount", label: "Долг", render: formatMoney },
      ];
      const paymentColumns = [
        { key: "payment_type", label: "Тип оплаты" },
        { key: "payments_count", label: "Платежей" },
        { key: "amount", label: "Сумма", render: formatMoney },
      ];
      const topColumns = [
        { key: "product_name", label: "Товар" },
        { key: "barcode", label: "Штрихкод" },
        { key: "quantity", label: "Кол-во", render: formatMoney },
        { key: "revenue", label: "Выручка", render: formatMoney },
        { key: "cogs", label: "Себестоимость", render: formatMoney },
        { key: "gross_profit", label: "Прибыль", render: formatMoney },
        { key: "margin_pct", label: "Маржа", render: formatPct },
      ];

      const content = `
        ${summaryHtml}
        ${buildHtmlTable("По магазинам", byStore, byStoreColumns)}
        ${buildHtmlTable("По клиентам", byCustomer, byCustomerColumns)}
        ${buildHtmlTable("Динамика по дням", daily, dailyColumns)}
        ${buildHtmlTable("Оплаты по типу", paymentsByType, paymentColumns)}
        ${buildHtmlTable("Топ-20 товаров", topProducts, topColumns)}
      `;

      if (format === "pdf") {
        const w = window.open("", "_blank", "width=1200,height=900");
        if (!w) {
          throw new Error("Разрешите всплывающие окна для экспорта PDF");
        }
        w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(
          title
        )}</title><style>body{font-family:Arial,sans-serif;padding:16px;color:#111}table{width:100%;border-collapse:collapse;margin:10px 0 24px}th,td{border:1px solid #999;padding:6px;font-size:12px;text-align:left}h2,h3{margin:8px 0}</style></head><body>${content}<script>window.onload=function(){window.print();}</script></body></html>`);
        w.document.close();
      } else {
        const html = `<!doctype html><html><head><meta charset="utf-8"></head><body>${content}</body></html>`;
        const blob = new Blob([html], {
          type: "application/vnd.ms-excel;charset=utf-8",
        });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `wholesale-report-${startDate}-${endDate}.xls`;
        a.click();
        URL.revokeObjectURL(a.href);
      }
    } catch (e) {
      setExportError(e?.message || "Ошибка экспорта");
    } finally {
      setExportLoading(null);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.title}>Опт продажи — отчёт</div>
        <div style={styles.subtitle}>Выручка, себестоимость, прибыль, оплаты и долги</div>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.field}>
          <label style={styles.label}>Магазин</label>
          <Select
            value={storeId}
            onChange={setStoreId}
            options={[
              { value: "", label: "Все магазины" },
              ...stores.map((s) => ({
                value: String(s.id),
                label: s.name || `Магазин #${s.id}`,
              })),
            ]}
            isSearchable={false}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Клиент</label>
          <Select
            value={customerId}
            onChange={setCustomerId}
            options={[
              { value: "", label: "Все клиенты" },
              ...customers.map((c) => ({
                value: String(c.id),
                label: c.name || `Клиент #${c.id}`,
              })),
            ]}
            isSearchable={false}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>С даты</label>
          <DatePicker
            selected={parseIsoLocal(startDate)}
            onChange={(d) => setStartDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            customInput={<input style={styles.input} />}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>По дату</label>
          <DatePicker
            selected={parseIsoLocal(endDate)}
            onChange={(d) => setEndDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            customInput={<input style={styles.input} />}
          />
        </div>

        <div style={styles.toolbarButtons}>
          <button type="button" onClick={handleApply} style={styles.applyBtn}>
            Обновить
          </button>
          <button type="button" onClick={handleReset} style={styles.resetBtn}>
            Сбросить
          </button>
          <button
            type="button"
            onClick={() => handleExport("pdf")}
            style={styles.exportBtn}
            disabled={exportLoading === "pdf"}
          >
            {exportLoading === "pdf" ? "..." : "↓ PDF"}
          </button>
          <button
            type="button"
            onClick={() => handleExport("excel")}
            style={styles.exportBtn}
            disabled={exportLoading === "excel"}
          >
            {exportLoading === "excel" ? "..." : "↓ Excel"}
          </button>
        </div>
      </div>
      {exportError ? <div style={styles.error}>{exportError}</div> : null}

      {status === "loading" && <Loading text="Загрузка отчёта…" />}
      {status === "failed" && <div style={styles.error}>{error || "Ошибка загрузки отчёта"}</div>}

      <div style={styles.grid}>
        <KpiCard title="Выручка" value={formatMoney(totals.revenue)} />
        <KpiCard title="Себестоимость" value={formatMoney(totals.cogs)} />
        <KpiCard title="Валовая прибыль" value={formatMoney(totals.gross_profit)} />
        <KpiCard title="Маржа" value={formatPct(totals.margin_pct)} />
        <KpiCard title="Оплачено" value={formatMoney(totals.paid_amount)} />
        <KpiCard title="Долг" value={formatMoney(totals.debt_amount)} />
      </div>

      <div style={styles.chartsGrid}>
        <ChartCard title="Динамика: выручка / себестоимость / прибыль">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
              <XAxis dataKey="date" stroke="var(--c-text-dim)" />
              <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-dim)" />
              <Tooltip formatter={(v) => formatMoney(v)} />
              <Legend />
              <Line type="monotone" dataKey="revenue" name="Выручка" stroke="var(--c-s1)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="cogs" name="Себестоимость" stroke="var(--c-warning)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="gross_profit" name="Прибыль" stroke="var(--c-success)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Структура оплат">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={paymentPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} label />
              {paymentPieData.map((entry, index) => (
                <Cell key={`payment-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
              ))}
              <Tooltip formatter={(v) => formatMoney(v)} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Топ магазинов: выручка / прибыль / долг">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={storeChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
              <XAxis dataKey="name" stroke="var(--c-text-dim)" />
              <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-dim)" />
              <Tooltip formatter={(v) => formatMoney(v)} />
              <Legend />
              <Bar dataKey="revenue" name="Выручка" fill="var(--c-s1)" />
              <Bar dataKey="gross_profit" name="Прибыль" fill="var(--c-success)" />
              <Bar dataKey="debt" name="Долг" fill="var(--c-danger)" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Топ клиентов по долгу">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={customerDebtChartData} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
              <XAxis type="number" tickFormatter={formatAxisMoney} stroke="var(--c-text-dim)" />
              <YAxis type="category" dataKey="name" width={140} stroke="var(--c-text-dim)" />
              <Tooltip formatter={(v) => formatMoney(v)} />
              <Bar dataKey="debt" name="Долг" fill="var(--c-danger)" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Топ-10 товаров по выручке">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={topProductsChartData} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
              <XAxis type="number" tickFormatter={formatAxisMoney} stroke="var(--c-text-dim)" />
              <YAxis type="category" dataKey="name" width={180} stroke="var(--c-text-dim)" />
              <Tooltip formatter={(v) => formatMoney(v)} />
              <Bar dataKey="revenue" name="Выручка" fill="var(--c-s1)" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <Section title="По магазинам">
        <SimpleTable
          rows={byStore}
          columns={[
            { key: "store_name", label: "Магазин" },
            { key: "sales_count", label: "Продаж" },
            { key: "revenue", label: "Выручка", render: formatMoney },
            { key: "cogs", label: "Себестоимость", render: formatMoney },
            { key: "gross_profit", label: "Прибыль", render: formatMoney },
            { key: "margin_pct", label: "Маржа", render: formatPct },
            { key: "paid_amount", label: "Оплачено", render: formatMoney },
            { key: "debt_amount", label: "Долг", render: formatMoney },
          ]}
          emptyText="Нет данных"
        />
      </Section>

      <Section title="По клиентам">
        <SimpleTable
          rows={byCustomer}
          columns={[
            { key: "customer_name", label: "Клиент" },
            { key: "sales_count", label: "Продаж" },
            { key: "revenue", label: "Выручка", render: formatMoney },
            { key: "cogs", label: "Себестоимость", render: formatMoney },
            { key: "gross_profit", label: "Прибыль", render: formatMoney },
            { key: "margin_pct", label: "Маржа", render: formatPct },
            { key: "paid_amount", label: "Оплачено", render: formatMoney },
            { key: "debt_amount", label: "Долг", render: formatMoney },
          ]}
          emptyText="Нет данных"
        />
      </Section>

      <Section title="Динамика по дням">
        <SimpleTable
          rows={daily}
          columns={[
            { key: "date", label: "Дата" },
            { key: "sales_count", label: "Продаж" },
            { key: "revenue", label: "Выручка", render: formatMoney },
            { key: "cogs", label: "Себестоимость", render: formatMoney },
            { key: "gross_profit", label: "Прибыль", render: formatMoney },
            { key: "margin_pct", label: "Маржа", render: formatPct },
            { key: "paid_amount", label: "Оплачено", render: formatMoney },
            { key: "debt_amount", label: "Долг", render: formatMoney },
          ]}
          emptyText="Нет данных"
        />
      </Section>

      <Section title="Оплаты по типу">
        <SimpleTable
          rows={paymentsByType}
          columns={[
            { key: "payment_type", label: "Тип оплаты" },
            { key: "payments_count", label: "Платежей" },
            { key: "amount", label: "Сумма", render: formatMoney },
          ]}
          emptyText="Нет данных"
        />
      </Section>

      <Section title="Топ-20 товаров">
        <SimpleTable
          rows={topProducts}
          columns={[
            { key: "product_name", label: "Товар" },
            { key: "barcode", label: "Штрихкод" },
            { key: "quantity", label: "Кол-во", render: formatMoney },
            { key: "revenue", label: "Выручка", render: formatMoney },
            { key: "cogs", label: "Себестоимость", render: formatMoney },
            { key: "gross_profit", label: "Прибыль", render: formatMoney },
            { key: "margin_pct", label: "Маржа", render: formatPct },
          ]}
          emptyText="Нет данных"
        />
      </Section>
    </div>
  );
}

function KpiCard({ title, value }) {
  return (
    <div style={styles.kpiCard}>
      <div style={styles.kpiTitle}>{title}</div>
      <div style={styles.kpiValue}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={styles.section}>
      <div style={styles.sectionTitle}>{title}</div>
      <div>{children}</div>
    </section>
  );
}

function ChartCard({ title, children }) {
  return (
    <section style={styles.chartCard}>
      <div style={styles.chartTitle}>{title}</div>
      <div style={styles.chartBody}>{children}</div>
    </section>
  );
}

function SimpleTable({ rows, columns, emptyText }) {
  if (!rows.length) return <div style={styles.empty}>{emptyText}</div>;
  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={styles.th}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={`${idx}-${row.id || row.date || row.product_id || row.customer_id || row.store_id}`}>
              {columns.map((c) => (
                <td key={c.key} style={styles.td}>
                  {c.render ? c.render(row[c.key]) : row[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100%",
    background: "var(--c-bg)",
    color: "var(--c-text)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: "18px 24px 10px",
    borderBottom: "1px solid var(--c-border)",
  },
  title: { fontSize: 20, fontWeight: 700, color: "var(--c-text)" },
  subtitle: { marginTop: 4, fontSize: 14, color: "var(--c-text-dim)" },
  toolbar: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
    flexWrap: "wrap",
    padding: "12px 24px",
    borderBottom: "1px solid var(--c-border)",
    background: "var(--c-bg)",
  },
  field: { display: "flex", flexDirection: "column", gap: 4, minWidth: 170 },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--c-text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  input: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    width: "100%",
  },
  select: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    minWidth: 220,
  },
  toolbarButtons: { display: "flex", gap: 8, marginLeft: "auto" },
  applyBtn: {
    padding: "9px 18px",
    borderRadius: 99,
    border: "none",
    fontWeight: 600,
    cursor: "pointer",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
  },
  resetBtn: {
    padding: "9px 18px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    fontWeight: 600,
    cursor: "pointer",
    background: "transparent",
    color: "var(--c-text)",
  },
  exportBtn: {
    padding: "9px 14px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    fontWeight: 700,
    cursor: "pointer",
    background: "var(--c-card-hover)",
    color: "var(--c-text)",
  },
  notice: {
    margin: "10px 24px 0",
    padding: "10px 12px",
    fontSize: 15,
    borderRadius: 8,
    background: "var(--c-card-hover)",
    color: "var(--c-text-dim)",
  },
  error: {
    margin: "10px 24px 0",
    padding: "10px 12px",
    fontSize: 15,
    borderRadius: 8,
    background: "var(--c-danger-dim)",
    color: "var(--c-danger)",
    border: "1px solid var(--c-danger)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    padding: "14px 24px",
  },
  chartsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
    gap: 12,
    padding: "0 24px 14px",
  },
  chartCard: {
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--c-text)",
    padding: "12px 14px",
    borderBottom: "1px solid var(--c-border)",
  },
  chartBody: { padding: "12px 10px 8px" },
  kpiCard: {
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    padding: "12px 14px",
  },
  kpiTitle: { fontSize: 14, color: "var(--c-text-dim)" },
  kpiValue: { marginTop: 4, fontSize: 22, fontWeight: 700, color: "var(--c-text)" },
  section: { margin: "0 24px 12px", background: "var(--c-card)", border: "1px solid var(--c-border)", borderRadius: 16 },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--c-text)",
    padding: "12px 14px",
    borderBottom: "1px solid var(--c-border)",
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 760 },
  th: {
    textAlign: "left",
    fontSize: 14,
    fontWeight: 700,
    color: "var(--c-text-dim)",
    padding: "10px 12px",
    borderBottom: "1px solid var(--c-border)",
    background: "var(--c-card-hover)",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "9px 12px",
    borderBottom: "1px solid var(--c-border)",
    fontSize: 15,
    color: "var(--c-text)",
    whiteSpace: "nowrap",
  },
  empty: {
    padding: "16px 14px",
    color: "var(--c-text-dim)",
    fontSize: 15,
  },
};

